import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export default function FrameworkDetail() {
  const { fid } = useParams();
  const [f, setF] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/frameworks/${fid}`).then((r) => setF(r.data));
    api.get("/progress/mine").then((r) => {
      setDone(r.data.sections_completed.includes(`framework:${fid}`));
    });
  }, [fid]);

  const markDone = async () => {
    await api.post("/progress/mark", { section: `framework:${fid}` });
    setDone(true);
    toast.success("Marked as complete");
  };

  if (!f) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-10">
      <Link to="/app/frameworks" className="text-sm text-muted-foreground inline-flex items-center gap-2 hover:text-foreground" data-testid="framework-back">
        <ArrowLeft size={14} /> All frameworks
      </Link>
      <header className="fade-up border-b border-border pb-8">
        <div className="overline mb-3">{f.issuer} · {f.released}</div>
        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[0.95]">{f.short}</h1>
        <p className="text-xl text-foreground mt-3">{f.name}</p>
        <p className="text-muted-foreground mt-6 max-w-3xl text-lg leading-relaxed">{f.summary}</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="border border-border bg-white p-5 sticky top-6">
            <div className="overline mb-3">On this page</div>
            <ol className="space-y-2 text-sm">
              {f.sections.map((s, i) => (
                <li key={s.title}>
                  <a href={`#s-${i}`} className="text-muted-foreground hover:text-foreground block">
                    <span className="font-mono text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>{s.title}
                  </a>
                </li>
              ))}
            </ol>
            <button onClick={markDone} disabled={done} data-testid="framework-mark-done"
              className="mt-5 w-full flex items-center justify-center gap-2 py-2 text-sm border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors disabled:bg-secondary disabled:text-muted-foreground disabled:border-border">
              <CheckCircle size={14} weight={done ? "fill" : "regular"} />
              {done ? "Completed" : "Mark complete"}
            </button>
          </div>
        </aside>

        <div className="lg:col-span-9 order-1 lg:order-2 space-y-10">
          {f.sections.map((s, i) => (
            <section key={s.title} id={`s-${i}`} className="fade-up">
              <div className="overline mb-2">Section {String(i + 1).padStart(2, "0")}</div>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-4">{s.title}</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
